// src/pages/ProductOrders.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, deleteDoc, doc, getDocs, orderBy, query, updateDoc } from 'firebase/firestore';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tooltip,
  Typography
} from '@mui/material';
import { ArrowBack, Delete, ShoppingBag } from '@mui/icons-material';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'À traiter', color: 'warning' },
  { value: 'confirmed', label: 'Confirmée', color: 'info' },
  { value: 'preparing', label: 'En préparation', color: 'primary' },
  { value: 'ready', label: 'Prête', color: 'success' },
  { value: 'completed', label: 'Terminée', color: 'success' },
  { value: 'cancelled', label: 'Annulée', color: 'error' }
];

const getStatus = (status) => STATUS_OPTIONS.find((item) => item.value === status) || STATUS_OPTIONS[0];

const formatDate = (value) => {
  const date = value?.toDate?.() || (value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('fr-BE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

function ProductOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchOrders = async () => {
    setLoading(true);
    setError('');

    try {
      const ordersQuery = query(collection(db, 'product_orders'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(ordersQuery);
      setOrders(snapshot.docs.map((document) => ({ id: document.id, ...document.data() })));
    } catch (err) {
      console.error('Erreur chargement commandes boutique:', err);
      setError('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const updateOrderStatus = async (orderId, status) => {
    try {
      await updateDoc(doc(db, 'product_orders', orderId), {
        status,
        updatedAt: new Date()
      });
      setOrders((prev) => prev.map((order) => (order.id === orderId ? { ...order, status } : order)));
    } catch (err) {
      console.error('Erreur mise à jour commande:', err);
      setError('Impossible de mettre à jour le statut de la commande');
    }
  };

  const handleDeleteOrder = async (order) => {
    const label = order.product?.title || 'cette commande';
    const clientName = `${order.customer?.firstName || ''} ${order.customer?.lastName || ''}`.trim();
    const confirmMsg = clientName
      ? `Supprimer la commande "${label}" de ${clientName} ? Cette action est irréversible.`
      : `Supprimer la commande "${label}" ? Cette action est irréversible.`;
    if (!window.confirm(confirmMsg)) return;

    try {
      await deleteDoc(doc(db, 'product_orders', order.id));
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
      setSnackbar({ open: true, message: 'Commande supprimée', severity: 'success' });
    } catch (err) {
      console.error('Erreur suppression commande:', err);
      setSnackbar({ open: true, message: 'Impossible de supprimer la commande', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/shop')}>
            Boutique
          </Button>
          <ShoppingBag sx={{ fontSize: 32 }} />
          <Typography variant="h4">Commandes boutique</Typography>
        </Box>

        <Button variant="outlined" onClick={fetchOrders}>Actualiser</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h4" color="primary">{orders.length}</Typography>
            <Typography variant="body2" color="text.secondary">Commandes totales</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main">{orders.filter((order) => order.status === 'pending').length}</Typography>
            <Typography variant="body2" color="text.secondary">À traiter</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h4" color="primary.main">{orders.filter((order) => order.status === 'preparing').length}</Typography>
            <Typography variant="body2" color="text.secondary">En préparation</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">{orders.filter((order) => ['ready', 'completed'].includes(order.status)).length}</Typography>
            <Typography variant="body2" color="text.secondary">Prêtes / terminées</Typography>
          </Paper>
        </Grid>
      </Grid>

      {orders.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">Aucune commande pour le moment</Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {orders.map((order) => {
            const status = getStatus(order.status);
            return (
              <Grid item xs={12} key={order.id}>
                <Paper sx={{ p: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                    <Box>
                      <Typography variant="h6">{order.product?.title || 'Produit'}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Commandé le {formatDate(order.createdAt)}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip label={status.label} color={status.color} />
                      <Tooltip title="Supprimer la commande">
                        <IconButton color="error" onClick={() => handleDeleteOrder(order)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2">Client</Typography>
                      <Typography>{order.customer?.firstName} {order.customer?.lastName}</Typography>
                      <Typography variant="body2">{order.customer?.email}</Typography>
                      <Typography variant="body2">{order.customer?.phone || 'Téléphone non renseigné'}</Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2">Commande</Typography>
                      <Typography variant="body2">Prix : {Number(order.product?.price || 0).toFixed(2)}€</Typography>
                      <Typography variant="body2">Taille : {order.selection?.size || '—'}</Typography>
                      <Typography variant="body2">Couleur : {order.selection?.color || '—'}</Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2">Livraison / remarque</Typography>
                      <Typography variant="body2">{order.customer?.deliveryAddress || 'Adresse non renseignée'}</Typography>
                      {order.note && <Typography variant="body2" sx={{ mt: 1 }}>Note : {order.note}</Typography>}
                    </Grid>
                  </Grid>

                  <Box mt={2} display="flex" justifyContent="flex-end">
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel>Statut</InputLabel>
                      <Select
                        value={order.status || 'pending'}
                        label="Statut"
                        onChange={(event) => updateOrderStatus(order.id, event.target.value)}
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default ProductOrders;